<template>
  <multiselect
    v-model="internalValue"
    :class="classes"
    :options="options"
    :track-by="trackByKey"
    :label="labelKey"
    :placeholder="placeholder"
    :multiple="multiple"
    :disabled="disabled || loading"
    :searchable="searchable"
    :allow-empty="clearable"
    :close-on-select="!multiple"
    :open-direction="openDirection"
    :option-height="optionHeight"
    :show-labels="false"
    :limit="1"
    @search-change="searchChange"
  >
    <!-- Label template for SINGLE select : BEGIN -->
    <template slot="singleLabel" slot-scope="props">
      {{ getLabel(props.option) }}
    </template>
    <!-- Label template for SINGLE select : END -->
    <!-- Label template for MULTIPLE select : BEGIN -->
    <template slot="selection" slot-scope="{ values, isOpen }">
      <div class="multiselect__single" v-if="values.length && (!isOpen || !searchable)">
        {{ value.length > 1 ? `${multiSelectionText} • ${value.length}` : getLabel(values[0]) }}
      </div>
      <div v-else></div>
    </template>
    <!-- Label template for MULTIPLE select : BEGIN -->
    <template slot="option" slot-scope="props">
      <!-- Option template for SINGLE select : BEGIN -->
      <div
        v-if="!multiple"
        class="s-option"
      >
        <div class="s-option__icon" v-if="props.option.icon">
          <Icon :name="props.option.icon"></Icon>
        </div>
        <div class="s-option__text">
          {{ getLabel(props.option) }}
        </div>
      </div>
      <!-- Option template for SINGLE select : END -->
      <!-- Option template for MULTIPLE select : BEGIN -->
      <div
        v-else
        class="s-option"
      >
        <div class="s-option__check">
          <Icon name="check-bold"></Icon>
        </div>
        <div class="s-option__text">
          {{ getLabel(props.option) }}
        </div>
      </div>
      <!-- Option template for MULTIPLE select : END -->
    </template>
    <!-- Template for no result : BEGIN -->
    <span
      slot="noResult"
      class="multiselect__info"
      v-html="noResultText"
    >
    </span>
    <!-- Template for no result : END -->
    <!-- Template for no options : BEGIN -->
    <span
      slot="noOptions"
      class="multiselect__info"
      v-html="noOptionsText"
    >
    </span>
    <!-- Template for no options : END -->
    <template slot="caret" slot-scope="{ toggle }">
      <div class="multiselect__icons multiselect__icons_left">
        <div
          class="multiselect__icon"
          v-if="leftIcon"
        >
          <Icon :name="leftIcon"></Icon>
        </div>
      </div>
      <div class="multiselect__icons multiselect__icons_right">
        <div class="multiselect__icon multiselect__icon-error">
          <Icon name="exclamation-circle-s"></Icon>
        </div>
        <div class="multiselect__icon multiselect__icon-warning">
          <Icon name="warning-s"></Icon>
        </div>
        <div class="multiselect__icon multiselect__icon-success">
          <Icon name="check-bold"></Icon>
        </div>
        <div
          v-if="loading"
          class="multiselect__icon multiselect__icon-spinner"
        >
        </div>
        <div
          class="multiselect__icon multiselect__icon_hover multiselect__caret"
          v-show="!showClearBtn"
          @mousedown.prevent.stop="toggle"
        >
          <Icon name="down"></Icon>
        </div>
        <div
          class="multiselect__icon multiselect__icon_hover multiselect__clear"
          v-show="showClearBtn"
          @mousedown.prevent.stop="clear"
        >
          <Icon name="close-circle-s"></Icon>
        </div>
      </div>
    </template>
  </multiselect>
</template>

<script>
import Icon from '@rr-component-library/icon/src/main';
import Multiselect from 'vue-multiselect';

export default {
  name: 'Select',
  components: {
    Multiselect,
    Icon,
  },
  model: {
    prop: 'value',
  },
  props: {
    value: {
      default: '',
    },
    /**
     *  Опции селекта
     */
    options: {
      type: Array,
      default: () => [],
      required: true,
    },
    /**
     *  Placeholder селекта
     */
    placeholder: {
      type: String,
      default: 'Выберите опцию',
    },
    /**
     *  Ключ идентификатора опции
     */
    trackBy: {
      type: String,
      default: 'id',
    },
    /**
     *  Ключ значения опции
     */
    label: {
      type: String,
      default: 'name',
    },
    /**
     *  Направление открытия дропдауна
     */
    openDirection: {
      type: String,
      default: 'auto',
      validator(value) {
        return ['auto', 'top', 'bottom'].indexOf(value) !== -1;
      },
    },
    /**
     *  Высота опции
     */
    optionHeight: {
      type: Number,
      default: 40,
    },
    /**
     *  Размер селекта
     */
    size: {
      type: String,
      default: 'md',
      validator(value) {
        return ['', 'sm', 'md'].indexOf(value) !== -1;
      },
    },
    /**
     *  Состояние
     */
    state: {
      type: String,
      validator(value) {
        return ['', 'error', 'warning', 'success'].indexOf(value) !== -1;
      },
    },
    /**
     *  Мультивыбор
     */
    multiple: Boolean,
    /**
     *  Делает селект недоступным
     */
    disabled: Boolean,
    /**
     *  Состояние загрузки.<br>
     *  Добавляет иконку прелоадера.
     */
    loading: Boolean,
    /**
     *  Добавляет возможность поиска опции
     */
    searchable: Boolean,
    /**
     * Добавляет крестик для удаления значения инпута
     */
    clearable: Boolean,
    /**
     * Включает перенос текста в опциях
     */
    wrapText: Boolean,
    /**
     * Иконка слева
     */
    iconLeft: String,
    /**
     * Авто-ширина для дропдауна
     */
    dropdownAutoWidth: Boolean,
    /**
     * Увеличенный размер на мобильных устройствах
     */
    mobile: Boolean,
    /**
     * Текст в слоте noOptions (когда нет опций)
     */
    noOptionsText: {
      type: String,
      default: 'Загрузка...',
    },
    /**
     * Текст в слоте noResult (когда нет результатов поиска)
     */
    noResultText: {
      type: String,
      default: 'Ничего не найдено',
    },
    /**
     * Текст когда выбрано несколько опций
     */
    multiSelectionText: {
      type: String,
      default: 'Выбрано',
    },
  },
  computed: {
    trackByKey() {
      return this.isArrayOfObjects ? this.trackBy : null;
    },
    labelKey() {
      return this.isArrayOfObjects ? this.label : null;
    },
    isArrayOfObjects() {
      const el = this.options && this.options[0];
      if (!el) {
        return false;
      }
      return typeof this.options[0] === 'object' && this.options[0] !== null;
    },
    leftIcon() {
      return (this.internalValue && this.internalValue.icon) || this.iconLeft;
    },
    internalValue: {
      get() {
        let { value } = this;
        const key = this.trackBy;
        if (key && this.isArrayOfObjects && value) {
          value = Array.isArray(value)
            ? value.map((v) => this.options.find((o) => o[key] === v))
            : this.options.find((o) => o[key] === value);
        }
        return value;
      },
      set(val) {
        let value = val;
        const key = this.trackBy;
        if (key && this.isArrayOfObjects && value) {
          value = Array.isArray(value)
            ? value.map((o) => o[key])
            : value[key];
        }
        this.$emit('input', value);
        this.$emit('inputInitial', val);
      },
    },
    isEmpty() {
      const val = this.internalValue;
      return !val || val.length === 0;
    },
    showClearBtn() {
      return this.clearable && !this.isEmpty && !this.disabled && !this.loading;
    },
    classes() {
      return {
        multiselect_sm: this.size === 'sm',
        multiselect_md: this.size === 'md',
        'has-error': this.state === 'error',
        'has-warning': this.state === 'warning',
        'has-success': this.state === 'success',
        multiselect_wrap: this.wrapText,
        'multiselect_dropdown-auto-width': this.dropdownAutoWidth,
        multiselect_mobile: this.mobile,
      };
    },
  },
  methods: {
    getLabel(option) {
      if (this.isArrayOfObjects) {
        return option[this.label];
      }
      return option;
    },
    clear() {
      this.internalValue = null;
    },
    searchChange(query) {
      this.$emit('search', query);
    },
  },
};
</script>

<style lang="scss">
@import "./Select";
</style>
